import React from 'react';
import moment from 'moment';

export interface CompareDay {
  date: string;
  attendancePercentage: number;
  presentCount: number;
  absentCount: number;
  otherCount: number;
}

export interface CompareSeries {
  id: string;
  label: string;
  color: string;
  days: CompareDay[];
}

interface Props {
  series: CompareSeries[];
}

const WIDTH = 1200;
const HEIGHT = 320;
const PAD_LEFT = 48;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 40;
const PLOT_W = WIDTH - PAD_LEFT - PAD_RIGHT;
const PLOT_H = HEIGHT - PAD_TOP - PAD_BOTTOM;

const NAVY = '#13325e';
const LABEL_GRAY = '#8a93a3';
const MAX_X_LABELS = 14;

const CompareGraph: React.FC<Props> = ({ series }) => {
  const allDates = Array.from(
    new Set(series.flatMap(s => s.days.map(d => d.date)))
  ).sort();

  if (allDates.length === 0) return null;

  const t0 = moment(allDates[0], 'YYYY-MM-DD').valueOf();
  const t1 = moment(allDates[allDates.length - 1], 'YYYY-MM-DD').valueOf();
  const span = Math.max(t1 - t0, 1);

  const x = (t: number) => PAD_LEFT + ((t - t0) / span) * PLOT_W;
  const y = (pct: number) => PAD_TOP + (1 - pct / 100) * PLOT_H;

  const weekLines: number[] = [];
  for (
    let w = moment(t0).startOf('isoWeek').add(1, 'week');
    w.valueOf() < t1;
    w.add(1, 'week')
  ) {
    if (w.valueOf() > t0) weekLines.push(w.valueOf());
  }

  const labelEvery = Math.max(1, Math.ceil(allDates.length / MAX_X_LABELS));

  return (
    <div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{ width: '100%', display: 'block' }}
        role='img'
        aria-label='Attendance comparison chart'
      >
        {[0, 25, 50, 75, 100].map(pct => (
          <g key={pct}>
            <line
              x1={PAD_LEFT} y1={y(pct)} x2={WIDTH - PAD_RIGHT} y2={y(pct)}
              stroke={pct === 0 ? NAVY : '#eef1f6'}
              strokeWidth={pct === 0 ? 2 : 1}
            />
            <text
              x={PAD_LEFT - 8} y={y(pct) + 4}
              textAnchor='end' fontSize={11} fontWeight={600} fill={LABEL_GRAY}
            >
              {pct}%
            </text>
          </g>
        ))}

        <line
          x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={PAD_TOP + PLOT_H}
          stroke={NAVY} strokeWidth={2}
        />

        {weekLines.map(t => (
          <line
            key={t}
            x1={x(t)} y1={PAD_TOP} x2={x(t)} y2={PAD_TOP + PLOT_H}
            stroke='#e0e0e0' strokeWidth={1}
          />
        ))}

        {series.map(s => {
          const plotted = s.days.filter(d => d.attendancePercentage != null);
          if (plotted.length === 0) return null;
          const pts = plotted
            .map(d => `${x(moment(d.date, 'YYYY-MM-DD').valueOf()).toFixed(1)},${y(d.attendancePercentage).toFixed(1)}`)
            .join(' ');
          return (
            <g key={s.id}>
              {plotted.length > 1 && (
                <polyline points={pts} fill='none' stroke={s.color} strokeWidth={2.5} />
              )}
              {plotted.map(d => {
                const t = moment(d.date, 'YYYY-MM-DD').valueOf();
                return (
                  <circle
                    key={d.date}
                    cx={x(t)} cy={y(d.attendancePercentage)} r={3.5}
                    fill={s.color} stroke='#fff' strokeWidth={1.5}
                  >
                    <title>{`${s.label} — ${moment(d.date, 'YYYY-MM-DD').format('D MMM YYYY')}: ${Math.round(d.attendancePercentage)}% (${d.presentCount} present, ${d.absentCount} absent, ${d.otherCount} other)`}</title>
                  </circle>
                );
              })}
            </g>
          );
        })}

        {allDates.map((date, i) => {
          if (i % labelEvery !== 0 && i !== allDates.length - 1) return null;
          const t = moment(date, 'YYYY-MM-DD').valueOf();
          return (
            <text
              key={date}
              x={x(t)} y={PAD_TOP + PLOT_H + 18}
              textAnchor='middle' fontSize={11} fontWeight={600} fill={LABEL_GRAY}
            >
              {moment(date, 'YYYY-MM-DD').format('D MMM')}
            </text>
          );
        })}
      </svg>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', padding: '6px 48px' }}>
        {series.map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width={24} height={12} style={{ flexShrink: 0 }}>
              <line x1={0} y1={6} x2={24} y2={6} stroke={s.color} strokeWidth={2.5} />
              <circle cx={12} cy={6} r={3.5} fill={s.color} stroke='#fff' strokeWidth={1} />
            </svg>
            <span style={{ fontSize: 13, color: '#555' }}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export { CompareGraph };
